/**
 * BookingIframeModal Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Modal wrapper for Icnea PMS booking iframe. Provides full-screen booking interface
 * with close button and responsive design. Integrates with ICNEA SOAP API.
 * 
 * Features:
 * - Full-screen modal with iframe
 * - Close button (X)
 * - Responsive sizing
 * - Portal rendering (outside normal DOM)
 * - Smooth animations
 * - Escape key support
 * - Background click to close
 * 
 * Props:
 * - isOpen: boolean (modal visibility)
 * - onClose: () => void (close handler)
 * - bookingUrl: string (Icnea iframe URL with parameters)
 * - propertyName: string (property name)
 * 
 * Dependencies:
 * - React 19.2.4 (hooks, portals)
 * - Framer Motion 12 (animations)
 * - Font Awesome 6.5 (icons)
 * - TailwindCSS 4.1 (responsive styling)
 * 
 * Booking URL Parameters:
 * - company_id: from VITE_ICNEA_COMPANY_ID env var
 * - start_date: check-in date (YYYY-MM-DD)
 * - end_date: check-out date (YYYY-MM-DD)
 * - num_guests: total guest count
 * - property_id: Icnea property ID
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with Icnea iframe
 */

import React, { useEffect, useMemo } from 'react';
import { ProIcon } from './ProIcon';
import { PortalModal } from './PortalModal';
import { trackBookingIframe } from '../lib/analytics';

interface BookingIframeModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingUrl: string;
  propertyName: string;
}

export const BookingIframeModal: React.FC<BookingIframeModalProps> = ({
  isOpen,
  onClose,
  bookingUrl,
  propertyName,
}) => {
  const bookingHost = useMemo(() => {
    try {
      return new URL(bookingUrl).host;
    } catch {
      return '';
    }
  }, [bookingUrl]);

  useEffect(() => {
    if (!isOpen) return;

    trackBookingIframe('open', {
      property_name: propertyName,
      booking_host: bookingHost,
    });

    const handleMessage = (event: MessageEvent) => {
      if (!bookingHost || !event.origin.includes(bookingHost)) return;

      trackBookingIframe('message', {
        property_name: propertyName,
        booking_host: bookingHost,
        message_origin: event.origin,
      });
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      trackBookingIframe('close', {
        property_name: propertyName,
        booking_host: bookingHost,
      });
    };
  }, [isOpen, propertyName, bookingHost]);

  return (
    <PortalModal isOpen={isOpen} onClose={onClose}>
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl h-[85vh] bg-white ui-radius-panel ui-shadow-panel overflow-hidden border border-slate-200 flex flex-col">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-black text-brand-ocean uppercase tracking-wider">Secure Booking</h3>
              <p className="text-xs text-brand-gray font-semibold">Powered by Icnea booking engine</p>
            </div>
            <button
              onClick={onClose}
              className="ui-btn-icon w-10 h-10"
              aria-label="Close booking modal"
            >
              <ProIcon icon="xmark" family="light" />
            </button>
          </div>

          <div className="flex-1 bg-slate-100">
            <iframe
              src={bookingUrl}
              title={`${propertyName} booking iframe`}
              className="w-full h-full border-0"
              loading="lazy"
              onLoad={() =>
                trackBookingIframe('loaded', {
                  property_name: propertyName,
                  booking_host: bookingHost,
                })
              }
              onError={() =>
                trackBookingIframe('error', {
                  property_name: propertyName,
                  booking_host: bookingHost,
                })
              }
            />
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackBookingIframe('open', {
                  property_name: propertyName,
                  booking_host: bookingHost,
                  mode: 'new_tab',
                })
              }
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-black text-brand-ocean hover:text-brand-sun transition-colors"
            >
              Open In New Tab <ProIcon icon="up-right-from-square" family="light" className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
